<template>
  <v-app :dark="nightMode">

    <!-- global loader -->
    <fullpage-loader
      :message="loadingMessage"
      v-if="loadingMessage"
    />

    <app-bar v-if="showAppBar && user &&  !loadingMessage" />
    <navigation-drawer v-if="user && !loadingMessage" />
    <v-content v-if="user && !loadingMessage">

      <!-- Loader -->
      <v-container fluid v-if="fetchingGeneralData">
        <v-layout>
          <v-flex>
            <v-progress-linear
              indeterminate
              height="2"
            ></v-progress-linear>
          </v-flex>
        </v-layout>
      </v-container>

      <!-- Content -->
      <router-view v-if="generalDataFetched && !fetchingGeneralData" />
    </v-content>

    <!-- Dialogs -->
    <bug-report-dialog />
    <add-event-dialog v-if="addEventDialog"  />
    <close-event-dialog  />
    <event-duties-dialog />
    <entity-edit-dialog v-if="entityEditDialog" />
    <!-- Global Snackbar -->
    <snackbar />
    <!-- Auth -->
    <auth v-if="!user && !loadingMessage" />
  </v-app>
</template>

<script>
import AppBar from "@/components/AppBar";
import NavigationDrawer from "@/components/NavigationDrawer";
import BugReportDialog from "@/components/BugReportDialog";
import AddEventDialog from "@/components/events/AddEventDialog";
import CloseEventDialog from "@/components/events/CloseEventDialog";
import EventDutiesDialog from "@/components/events/EventDutiesDialog";

import EntityEditDialog from "@/components/EntityEditDialog";
import Snackbar from "@/components/Snackbar";
import Auth from "@/components/Auth";
import FullpageLoader from "@/components/FullpageLoader";

import jwtDecode from "jwt-decode";

import firebase from "firebase/app";
import firebaseAuth from "firebase/auth";

import { getApi } from "./api";

export default {
  data: () => ({
    fetchingGeneralData: false,
    generalDataFetched: false,
    checkingIfFirebaseUserExists: false,
    gettingFirebaseUserData: false
  }),

  computed: {
    nightMode() {
      return this.$store.getters["nightMode"];
    },
    entityEditDialog: {
      get() {
        return this.$store.state.entityEditDialog;
      },
      set(payload) {
        this.$store.commit("TOGGLE_ENTITY_EDIT_DIALOG", payload);
      }
    },
    addEventDialog: {
      get() {
        return this.$store.getters["events/addEventDialog"];
      },
      set(isActive) {
        this.$store.commit("events/TOGGLE_ADD_EVENT_DIALOG", isActive);
      }
    },
    closeEventDialog: {
      get() {
        return this.$store.getters["events/closeEventDialog"];
      },
      set(isActive) {
        this.$store.commit("events/TOGGLE_CLOSE_EVENT_DIALOG", isActive);
      }
    },
    eventDutiesDialog: {
      get() {
        return this.$store.getters["events/eventDutiesDialog"];
      },
      set(isActive) {
        this.$store.commit("events/TOGGLE_EVENT_DUTIES_DIALOG", isActive);
      }
    },
    showAppBar() {
      return this.$route.path !== "/login";
    },
    user() {
      return this.$store.getters["user/user"];
    },
    firebaseUser() {
      return this.$store.getters["user/firebaseUser"];
    },
    loadingMessage() {
      if (this.checkingIfFirebaseUserExists) {
        return "מחפש משתמש מחובר...";
      } else if (this.gettingFirebaseUserData) {
        return "מאתחל מידע של המשתמש...";
      }
    }
  },

  methods: {
    initApp() {
      this.fetchGeneralData();
      getApi();
    },
    fetchGeneralData() {
      this.fetchingGeneralData = true;
      let funcArray = [];
      let resourcesNames = [
        "partnerType",
        "partnerRelationship",
        "contactDetailType",
        "standard",
        "standardGroup",
        "eventType",
        "eventSubType",
        "eventSource",
        "evacuationType",
        "role",
        "language",
        "country",
        "region",
        "mapMarkerType",
        "insuranceCompany",
        "hashtag",
        "user",
        "gender",
        "aran"
      ];
      for (let resourceName of resourcesNames) {
        let resource = this.$store.state.resources[resourceName];
        funcArray.push(
          this.$store.dispatch("API_GET", {
            url: resource.endpoint,
            entityType: resource.entityType,
            schema: resource.listSchema,
            params: Object.assign({ limit: 0 }, resource.getParams)
          })
        );
      }

      funcArray.push(
        this.$store.dispatch("API_GET", {
          url: "events",
          entityType: "events",
          schema: "eventListSchema",
          params: Object.assign(
            { limit: 0 },
            {
              q: JSON.stringify({
                endDate: null
              })
            }
          )
        })
      );

      Promise.all(funcArray)
        .then(() => {
          this.generalDataFetched = true;
        })
        .catch(err => {})
        .finally(() => {
          this.fetchingGeneralData = false;
        });
    },
    firebaseAuth() {
      let self = this;
      let interval;
      this.checkingIfFirebaseUserExists = true;

      firebase.auth().onIdTokenChanged(function(firebaseUser) {
        self.checkingIfFirebaseUserExists = false;
        if (firebaseUser) {
          let user = self.$store.getters["user/user"];
          if (!user) {
            self.gettingFirebaseUserData = true;
            firebaseUser
              .getIdToken()
              .then(idToken => {
                self.$store.commit("user/SET_FIREBASE_USER", firebaseUser);
                self.$store
                  .dispatch("user/GET_USER_BY_FIREBASE_ID", firebaseUser.uid)
                  .catch(err => {
                    console.log(err);
                    self.$store.dispatch("user/SIGN_OUT");
                    self.$store.dispatch("SHOW_SNACKBAR", {
                      text: "המשתמש לא מוגדר במערכת. יש ליצור קשר עם מנהל",
                      color: "error"
                    });
                  })
                  .finally(() => {
                    self.gettingFirebaseUserData = false;
                  });
              })
              .catch(error => {
                console.log(error);
                self.$store.commit("user/SET_FIREBASE_USER", null);
              });
          }
        } else {
          // No user is signed in.

          if (interval) {
            clearInterval(interval);
            interval = null;
          }

          self.$store.commit("user/SET_FIREBASE_USER", null);
        }
      });
    },
    setEventLayoutByWidth() {
      if (window) {
        let width = window.innerWidth;
        if (width < 1024) {
          this.$store.commit("events/SET_EVENT_PAGE_LAYOUT", "1");
        } else if (width < 1400) {
          this.$store.commit("events/SET_EVENT_PAGE_LAYOUT", "1-1");
        } else if (width < 1800) {
          this.$store.commit("events/SET_EVENT_PAGE_LAYOUT", "2-1");
        } else {
          this.$store.commit("events/SET_EVENT_PAGE_LAYOUT", "1-2-1");
        }
      }
    }
  },

  sockets: {

  },

  mounted() {
    this.firebaseAuth();
    this.setEventLayoutByWidth();
  },

  watch: {
    user: function(val) {
      if (val) {
        this.initApp();
      }
    }
  },

  components: {
    AppBar,
    NavigationDrawer,
    BugReportDialog,
    AddEventDialog,
    CloseEventDialog,
    EventDutiesDialog,
    EntityEditDialog,
    Snackbar,
    Auth,
    FullpageLoader
  }
};
</script>

<style>
html {
  unicode-bidi: plaintext !important;
}

.fullscreen {
  width: 100%;
  height: 100%;
  z-index: 1000;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  overflow-y: scroll;
}

td a {
  text-decoration: none;
}

/* rtl support fixes */
.application--is-rtl .v-list__tile__title {
  text-align: right;
}

.v-chip.v-chip.v-chip--outline .v-avatar {
  margin-left: 0;
}

.v-expansion-panel__container {
  text-align: right;
}

ol,
ul {
  padding-right: 24px;
  margin-bottom: 18px;
}

.v-alert__icon {
  margin-right: unset;
  margin-left: 16px;
}

.flip-horizontal {
  -moz-transform: scaleX(-1);
  -webkit-transform: scaleX(-1);
  -o-transform: scaleX(-1);
  transform: scaleX(-1);
  -ms-filter: fliph; /*IE*/
  filter: fliph; /*IE*/
}

.noselect {
  -webkit-touch-callout: none; /* iOS Safari */
    -webkit-user-select: none; /* Safari */
     -khtml-user-select: none; /* Konqueror HTML */
       -moz-user-select: none; /* Firefox */
        -ms-user-select: none; /* Internet Explorer/Edge */
            user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome and Opera */
}
</style>


// WEBPACK FOOTER //
// src/App.vue